if (!customElements.get('upsell-gallery')) {
  customElements.define(
    'upsell-gallery',
    class UpsellGallery extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;

        this.trigger = this.querySelector('.upsell-gallery__trigger');
        this.dialog = this.querySelector('[data-gallery-dialog]');
        this.closeButton = this.querySelector('[data-gallery-close]');
        this.previousButton = this.querySelector('[data-gallery-previous]');
        this.nextButton = this.querySelector('[data-gallery-next]');
        this.stage = this.querySelector('[data-gallery-stage]');
        this.mainImage = this.querySelector('[data-gallery-main-image]');
        this.status = this.querySelector('[data-gallery-status]');
        this.thumbnails = Array.from(this.querySelectorAll('[data-gallery-thumbnail]'));

        if (!this.trigger || !this.dialog || !this.mainImage || this.thumbnails.length === 0) return;

        this.initialized = true;
        this.activeIndex = 0;
        this.touchStartX = null;

        this.handleOpen = this.open.bind(this);
        this.handleClose = this.close.bind(this);
        this.handleDialogClose = this.onDialogClose.bind(this);
        this.handleDialogClick = this.onDialogClick.bind(this);
        this.handleKeydown = this.onKeydown.bind(this);
        this.handleTouchStart = this.onTouchStart.bind(this);
        this.handleTouchEnd = this.onTouchEnd.bind(this);
        this.handlePrevious = this.showPrevious.bind(this);
        this.handleNext = this.showNext.bind(this);
        this.stopUpsellToggle = (event) => event.stopPropagation();
        this.thumbnailHandlers = this.thumbnails.map((thumbnail, index) => {
          return () => this.setActiveImage(index);
        });

        this.addEventListener('click', this.stopUpsellToggle);
        this.trigger.addEventListener('click', this.handleOpen);
        this.closeButton.addEventListener('click', this.handleClose);
        this.previousButton.addEventListener('click', this.handlePrevious);
        this.nextButton.addEventListener('click', this.handleNext);
        this.dialog.addEventListener('close', this.handleDialogClose);
        this.dialog.addEventListener('click', this.handleDialogClick);
        this.dialog.addEventListener('keydown', this.handleKeydown);
        this.stage.addEventListener('touchstart', this.handleTouchStart, { passive: true });
        this.stage.addEventListener('touchend', this.handleTouchEnd, { passive: true });

        this.thumbnails.forEach((thumbnail, index) => {
          thumbnail.addEventListener('click', this.thumbnailHandlers[index]);
        });
      }

      disconnectedCallback() {
        if (!this.initialized) return;

        this.removeEventListener('click', this.stopUpsellToggle);
        this.trigger.removeEventListener('click', this.handleOpen);
        this.closeButton.removeEventListener('click', this.handleClose);
        this.previousButton.removeEventListener('click', this.handlePrevious);
        this.nextButton.removeEventListener('click', this.handleNext);
        this.dialog.removeEventListener('close', this.handleDialogClose);
        this.dialog.removeEventListener('click', this.handleDialogClick);
        this.dialog.removeEventListener('keydown', this.handleKeydown);
        this.stage.removeEventListener('touchstart', this.handleTouchStart);
        this.stage.removeEventListener('touchend', this.handleTouchEnd);
        this.thumbnails.forEach((thumbnail, index) => {
          thumbnail.removeEventListener('click', this.thumbnailHandlers[index]);
        });
        this.initialized = false;
      }

      open(event) {
        event.preventDefault();
        const matchingIndex = this.findTriggerImageIndex();
        this.setActiveImage(matchingIndex >= 0 ? matchingIndex : this.activeIndex, false);

        if (typeof this.dialog.showModal === 'function') {
          this.dialog.showModal();
          document.documentElement.classList.add('upsell-gallery-open');
          window.requestAnimationFrame(() => this.closeButton.focus());
        }
      }

      close() {
        if (this.dialog.open) this.dialog.close();
      }

      onDialogClose() {
        document.documentElement.classList.remove('upsell-gallery-open');
        this.trigger.focus();
      }

      onDialogClick(event) {
        if (event.target === this.dialog) this.close();
      }

      onKeydown(event) {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.showPrevious();
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.showNext();
        } else if (event.key === 'Home') {
          event.preventDefault();
          this.setActiveImage(0);
        } else if (event.key === 'End') {
          event.preventDefault();
          this.setActiveImage(this.thumbnails.length - 1);
        }
      }

      onTouchStart(event) {
        this.touchStartX = event.changedTouches[0]?.clientX ?? null;
      }

      onTouchEnd(event) {
        if (this.touchStartX === null || this.thumbnails.length <= 1) return;

        const touchEndX = event.changedTouches[0]?.clientX;
        if (typeof touchEndX !== 'number') return;

        const distance = touchEndX - this.touchStartX;
        this.touchStartX = null;
        if (Math.abs(distance) < 50) return;

        if (distance < 0) {
          this.showNext();
        } else {
          this.showPrevious();
        }
      }

      showPrevious() {
        if (this.thumbnails.length <= 1) return;
        const previousIndex = (this.activeIndex - 1 + this.thumbnails.length) % this.thumbnails.length;
        this.setActiveImage(previousIndex);
      }

      showNext() {
        if (this.thumbnails.length <= 1) return;
        const nextIndex = (this.activeIndex + 1) % this.thumbnails.length;
        this.setActiveImage(nextIndex);
      }

      setActiveImage(index, announce = true) {
        const thumbnail = this.thumbnails[index];
        if (!thumbnail) return;

        this.activeIndex = index;
        this.mainImage.classList.add('is-loading');
        this.mainImage.src = thumbnail.dataset.imageSrc;
        this.mainImage.alt = thumbnail.dataset.imageAlt || '';
        this.mainImage.width = Number(thumbnail.dataset.imageWidth) || this.mainImage.width;
        this.mainImage.height = Number(thumbnail.dataset.imageHeight) || this.mainImage.height;

        if (this.mainImage.complete) {
          this.mainImage.classList.remove('is-loading');
        } else {
          this.mainImage.addEventListener(
            'load',
            () => this.mainImage.classList.remove('is-loading'),
            { once: true }
          );
        }

        this.thumbnails.forEach((item, itemIndex) => {
          const isActive = itemIndex === index;
          item.classList.toggle('is-active', isActive);
          if (isActive) {
            item.setAttribute('aria-current', 'true');
          } else {
            item.removeAttribute('aria-current');
          }
        });

        if (this.status) {
          if (!announce) this.status.setAttribute('aria-live', 'off');
          const separator = this.status.dataset.labelOf || '/';
          this.status.textContent = `${index + 1} ${separator} ${this.thumbnails.length}`;
          window.requestAnimationFrame(() => this.status.setAttribute('aria-live', 'polite'));
        }

        thumbnail.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      }

      findTriggerImageIndex() {
        const triggerImage = this.trigger.querySelector('img');
        if (!triggerImage?.src) return -1;

        const triggerPath = this.normalizedImagePath(triggerImage.src);
        return this.thumbnails.findIndex((thumbnail) => {
          return this.normalizedImagePath(thumbnail.dataset.imageMatchSrc) === triggerPath;
        });
      }

      normalizedImagePath(url) {
        try {
          return new URL(url, window.location.href).pathname;
        } catch (error) {
          return url || '';
        }
      }
    }
  );
}
